import { FC, Fragment } from 'react';
import { t } from 'i18next';
import {
  Box,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
import { GetApp, Delete } from '@mui/icons-material';

import {
  DocumentsColumnNames,
  IDocumentsTableEntries,
  IDocumentsTableFilters,
  useDocumentsTable,
} from '@views/pages/Patient/components/Documents/hooks';

import { DocumentFile, IColumnDefinition } from '@common/types';
import dayjs from 'dayjs';
import { theme } from '@store';
import {
  TableNoResultPlaceholder,
  TruncatedTextWithConditionalTooltip,
  fileFormatMapper,
} from '@components';

interface IDocumentsTableProps {
  rows: IDocumentsTableEntries[];
  columns: IColumnDefinition[];
  filters: IDocumentsTableFilters;
  setFilters: (filters: IDocumentsTableFilters) => void;
  handleOpenDeleteFileModal: (file: DocumentFile) => void;
  handleOpenPreviewFileModal: (file: DocumentFile) => void;
  totalCount: number;
  loading: boolean;
  disabled: boolean;
  tabPanelInstrumentHeight: number;
  isReadOnly: boolean;
}

export const DocumentsTable: FC<IDocumentsTableProps> = ({
  rows,
  filters,
  columns,
  setFilters,
  totalCount,
  loading,
  disabled = false,
  handleOpenDeleteFileModal,
  handleOpenPreviewFileModal,
  tabPanelInstrumentHeight,
  isReadOnly,
}) => {
  const {
    sort_by,
    sort_order,
    onPageIndexChange,
    onPageSizeChange,
    handleChangeOrder,
    handleDownload,
    isDownloading,
  } = useDocumentsTable({ filters, setFilters });

  return (
    <>
      <TableContainer
        component={Paper}
        sx={{
          borderRadius: 0,
          boxShadow: 'none',
          border: '1px solid rgba(0, 0, 0, 0.12)',
          borderBottom: 'none',
          maxHeight: `calc(100vh - ${tabPanelInstrumentHeight + 157}px)`,
        }}
      >
        <Table stickyHeader size='small'>
          <TableHead>
            <TableRow
              sx={{
                borderRadius: 0,
                '& .MuiTableCell-head': {
                  paddingY: '1rem',
                  bgcolor: theme.palette.headerBackground.main,
                  overflow: 'hidden',
                  width: '600px',
                },
              }}
            >
              {columns.map((col, index) => (
                <TableCell key={`${col.id}-${index}`} align='left'>
                  <Box sx={{ minWidth: col.width }}>
                    {col.sortable ? (
                      <TableSortLabel
                        active={true}
                        direction={col.id === sort_by ? sort_order : 'asc'}
                        onClick={() =>
                          handleChangeOrder(col.id as DocumentsColumnNames)
                        }
                      >
                        {col.label}
                      </TableSortLabel>
                    ) : (
                      col.label
                    )}
                  </Box>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length > 0 && !loading && (
              <>
                {rows.map(row => {
                  return (
                    <Fragment key={row.id}>
                      <TableRow
                        onClick={() => {
                          handleOpenPreviewFileModal(row);
                        }}
                        sx={{
                          cursor: 'pointer',

                          '& .MuiTableCell-body': {
                            paddingY: '1rem',
                          },
                          '&:hover, &:nth-of-type(even):hover': {
                            backgroundColor: theme.palette.hoverTableRowBg.main,
                          },
                        }}
                      >
                        <TableCell sx={{ maxWidth: '220px' }} align='left'>
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                            }}
                          >
                            <Box sx={{ mb: '-0.5rem' }}>
                              <TruncatedTextWithConditionalTooltip
                                text={row.file_name}
                                variant='body2'
                                width='100%'
                              />
                            </Box>
                            <Typography
                              variant='caption'
                              sx={{ color: 'text.disabled' }}
                            >
                              {fileFormatMapper(row.file_format)}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ maxWidth: '220px' }} align='left'>
                          <Typography variant='body2'>{row.size}</Typography>
                        </TableCell>
                        <TableCell sx={{ maxWidth: '220px' }} align='left'>
                          <Box>
                            <Typography variant='body2'>
                              {t(`common.categoryOptions.${row.category}`)}
                            </Typography>
                            {row.specification && (
                              <TruncatedTextWithConditionalTooltip
                                text={row.specification}
                                color='text.disabled'
                                variant='caption'
                                width='100%'
                              />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ maxWidth: '220px' }} align='left'>
                          <Box>
                            <Typography variant='body2'>
                              {row.uploaded_by.name}
                            </Typography>
                            <Typography
                              variant='caption'
                              sx={{ color: 'text.disabled' }}
                            >
                              {t(`roles.${row.uploaded_by.role}`)}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ maxWidth: '220px' }} align='left'>
                          <Box>
                            <Typography variant='body2'>
                              {dayjs(row.uploaded_on).format('MMM DD, YYYY')}
                            </Typography>
                            <Typography
                              variant='caption'
                              sx={{ color: 'text.disabled' }}
                            >
                              {dayjs(row.uploaded_on).format('hh:mm A')}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell
                          sortDirection='desc'
                          component='th'
                          scope='row'
                          sx={{ maxWidth: '220px' }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.5rem',
                              justifyContent: 'flex-end',
                            }}
                          >
                            <IconButton
                              size='small'
                              disabled={isDownloading}
                              onClick={e => {
                                e.stopPropagation();
                                handleDownload(row.id, row.file_name);
                              }}
                            >
                              <GetApp sx={{ fontSize: '1.5rem' }} />
                            </IconButton>
                            <IconButton
                              size='small'
                              disabled={disabled || isReadOnly}
                              onClick={e => {
                                e.stopPropagation();
                                handleOpenDeleteFileModal(row);
                              }}
                            >
                              <Delete sx={{ fontSize: '1.5rem' }} />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    </Fragment>
                  );
                })}
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {rows.length === 0 && !loading && (
        <Box
          sx={{
            bgcolor: theme.palette.common.white,
            display: 'grid',
            placeItems: 'center',
            width: '100%',
            height: 'calc(100vh - 600px)',
            borderLeft: '1px solid rgba(0, 0, 0, 0.12)',
            borderRight: '1px solid rgba(0, 0, 0, 0.12)',
          }}
        >
          <TableNoResultPlaceholder />
        </Box>
      )}
      {loading && (
        <Box
          sx={{
            bgcolor: theme.palette.common.white,
            display: 'grid',
            placeItems: 'center',
            width: '100%',
            height: 'calc(100vh - 470px)',
            borderLeft: '1px solid rgba(0, 0, 0, 0.12)',
            borderRight: '1px solid rgba(0, 0, 0, 0.12)',
          }}
        >
          <CircularProgress size='64px' />
        </Box>
      )}
      <TablePagination
        sx={{
          bgcolor: theme.palette.common.white,
          paddingY: '0.75rem',
          zIndex: 100,
          border: '1px solid rgba(0, 0, 0, 0.12)',
        }}
        component='div'
        count={totalCount}
        page={filters.page - 1}
        rowsPerPage={filters.limit}
        onPageChange={onPageIndexChange}
        onRowsPerPageChange={onPageSizeChange}
      />
    </>
  );
};
